<template>
  <div>

    <Component
      v-if="preview"
      :is="preview.isExpandable ? 'button' : 'div'"
      :type="preview.isExpandable && 'button'"
      :class="[preview?.containerClasses || 'bg-slate-100', preview.isExpandable && 'block']"
      class="relative rounded-xl overflow-hidden h-16 w-16 min-w-16 group flex items-center justify-center focus:outline-none focus:ring ring-offset-2 ring-bdBlue-200"
      aria-label="Preview File"
      @click="handlePreview"
      @keydown.enter="handlePreview"
      @mouseenter="eagerLoadExpandedPreview"
      @focus="eagerLoadExpandedPreview"
    >
      <Component
        :is="preview.component"
        v-bind="preview.bind"
      />
      <span
        v-if="preview.readableMimeType"
        :class="preview.mimeContainerClasses"
        class="rounded-lg py-0.5 px-1 text-xxs font-bold uppercase text-white absolute bottom-1 right-1"
      >
        {{ preview.readableMimeType }}
      </span>
      <span
        v-if="preview.isExpandable"
        class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 text-base text-white group-hover:opacity-50 transition-all duration-500"
        >
        <FontAwesomeIcon
          :icon="faUpRightAndDownLeftFromCenter"
        />
      </span>
    </Component>

    <Component
      v-if="loadExpandedPreview"
      v-model:open="showExpandedPreview"
      :is="expandedPreview"
      :attachment="attachment"
    />

  </div>
</template>

<script setup>
import OptimizedImage from "./OptimizedImage.vue";
import {computed, defineAsyncComponent, ref} from "vue";
import {QUALITY_THUMBNAIL} from "../constants/FileQualityLevels.js";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {faUpRightAndDownLeftFromCenter} from "@fortawesome/free-solid-svg-icons";

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const loadExpandedPreview = ref(false);
const showExpandedPreview = ref(false);

const expandedPreview = defineAsyncComponent(() => import("./ExpandedPreview.vue"));

// Browser detection
const isChrome = computed(() => {
  return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
});

const preview = computed(() => {
  const mimeType = props.attachment.mime_type;
  if (!mimeType) return null;

  const isImage = mimeType.includes("image");

  if (isImage) {
    return {
      component: OptimizedImage,
      bind: {
        class: "object-cover min-w-full min-h-full w-auto h-auto group-hover:scale-110 transition-all",
        image: props.attachment,
        qualityLevel: QUALITY_THUMBNAIL,
      },
      isExpandable: true,
    };
  }

  const isPdf = mimeType === "application/pdf";
  const isDocx = mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  const isDoc = mimeType === "application/msword";
  const isTxt = mimeType === "text/plain";
  const isExcel = mimeType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const isCsv = mimeType === "text/csv";

  const isDocument = isDocx || isDoc || isTxt;
  const isPreViewable = isPdf || isDocx || isTxt;

  const iconRelative = isPdf ? "file-mime-pdf.png" : isDocument ? "file-mime-document.png" : "file-mime-unknown.png";
  const containerClasses = isPdf ? "bg-red-50" : isDocument ? "bg-blue-50" : "bg-yellow-50";
  const mimeContainerClasses = isPdf ? "bg-red-600" : isDocument ? "bg-blue-600" : "bg-slate-600";
  const readableMimeType = isPdf ? "PDF" : isDocx ? "DOCX" : isDoc ? "DOC" : isTxt ? "TXT" : isExcel ? "XLSX" : isCsv ? "CSV" : mimeType;
  const iconAbsolute = `https://resources.businessdraft.com/${iconRelative}`;

  return {
    component: "img",
    bind: {
      class: "h-5/6 w-auto",
      src: iconAbsolute,
      alt: `${mimeType} icon`,
    },
    containerClasses,
    mimeContainerClasses,
    readableMimeType,
    isExpandable: !!isPreViewable,
  };
});

const eagerLoadExpandedPreview = () => {
  if (!preview.value.isExpandable) return;
  loadExpandedPreview.value = true;
};

const handlePreview = (e) => {
  if (!preview.value.isExpandable) return;
  e.preventDefault();

  // Check if Chrome and if it's a PDF
  const mimeType = props.attachment.mime_type;
  const isPdf = mimeType === "application/pdf";

  // For Chrome users with PDFs, open in new tab
  if (isChrome.value && isPdf) {
    const url = props.attachment.high_quality_path || props.attachment.original_path;
    window.open(url, '_blank');
    return;
  }

  // Otherwise, use the existing modal behavior
  eagerLoadExpandedPreview();
  showExpandedPreview.value = true;
};

defineExpose({
  preview,
  handlePreview,
});
</script>
